import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { isAdminRole } from "@shared/roleAccessConfig";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import {
  MessageCircle,
  Send,
  MoreHorizontal,
  Edit2,
  Trash2,
  Reply,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { VoiceInputButton } from "@/components/common/VoiceInput";

interface Comment {
  id: number;
  newsId: number;
  userId: number;
  content: string;
  parentCommentId: number | null;
  mentionedUserIds: number[] | null;
  createdAt: string;
  updatedAt: string;
  isEdited: boolean;
  user: {
    id: number;
    name: string;
    photoUrl?: string;
    title?: string;
  };
}

interface CommentSectionProps {
  newsId: number;
  initialCommentCount?: number;
}

export function CommentSection({ newsId, initialCommentCount = 0 }: CommentSectionProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [editingComment, setEditingComment] = useState<number | null>(null);
  const [editContent, setEditContent] = useState("");
  const [showAllComments, setShowAllComments] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

  // Fetch comments
  const { data: comments = [], isLoading } = useQuery<Comment[]>({
    queryKey: [`/api/news/${newsId}/comments`],
    staleTime: 30000,
  });

  // Add comment mutation with optimistic update
  const addCommentMutation = useMutation({
    mutationFn: async (data: { content: string; parentCommentId?: number }) => {
      return await apiRequest("POST", `/api/news/${newsId}/comments`, data);
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [`/api/news/${newsId}/comments`] });
      const previous = queryClient.getQueryData<Comment[]>([`/api/news/${newsId}/comments`]);

      const optimisticComment: Comment = {
        id: Date.now(),
        newsId,
        userId: user?.id || 0,
        content: data.content,
        parentCommentId: data.parentCommentId || null,
        mentionedUserIds: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isEdited: false,
        user: {
          id: user?.id || 0,
          name: user?.name || "",
          photoUrl: user?.photoUrl || undefined,
          title: user?.title || undefined,
        },
      };

      queryClient.setQueryData<Comment[]>([`/api/news/${newsId}/comments`], (old = []) => {
        return [...old, optimisticComment];
      });

      return { previous };
    },
    onSuccess: () => {
      setNewComment("");
      setReplyContent("");
      setReplyingTo(null);
      toast({ title: "Comment posted" });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([`/api/news/${newsId}/comments`], context.previous);
      }
      toast({ title: "Error", description: "Could not post comment. Please try again.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/news/${newsId}/comments`] });
    },
  });

  // Update comment mutation
  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentId, content }: { commentId: number; content: string }) => {
      return await apiRequest("PUT", `/api/news/${newsId}/comments/${commentId}`, { content });
    },
    onMutate: async ({ commentId, content }) => {
      await queryClient.cancelQueries({ queryKey: [`/api/news/${newsId}/comments`] });
      const previous = queryClient.getQueryData<Comment[]>([`/api/news/${newsId}/comments`]);

      queryClient.setQueryData<Comment[]>([`/api/news/${newsId}/comments`], (old = []) => {
        return old.map(c => c.id === commentId ? { ...c, content, isEdited: true, updatedAt: new Date().toISOString() } : c);
      });

      return { previous };
    },
    onSuccess: () => {
      setEditingComment(null);
      setEditContent("");
      toast({ title: "Comment updated" });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([`/api/news/${newsId}/comments`], context.previous);
      }
      toast({ title: "Error", description: "Could not update comment.", variant: "destructive" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/news/${newsId}/comments`] });
    },
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: number) => {
      await apiRequest("DELETE", `/api/news/${newsId}/comments/${commentId}`);
    },
    onMutate: async (commentId) => {
      await queryClient.cancelQueries({ queryKey: [`/api/news/${newsId}/comments`] });
      const previous = queryClient.getQueryData<Comment[]>([`/api/news/${newsId}/comments`]);

      queryClient.setQueryData<Comment[]>([`/api/news/${newsId}/comments`], (old = []) => {
        return old.filter(c => c.id !== commentId && c.parentCommentId !== commentId);
      });

      return { previous };
    },
    onSuccess: () => {
      toast({ title: "Comment deleted" });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData([`/api/news/${newsId}/comments`], context.previous);
      }
      toast({ title: "Error", description: "Could not delete comment.", variant: "destructive" });
    },
    onSettled: () => {
      setDeletingCommentId(null);
      queryClient.invalidateQueries({ queryKey: [`/api/news/${newsId}/comments`] });
    },
  });

  const handleSubmitComment = () => {
    if (!newComment.trim()) return;
    addCommentMutation.mutate({ content: newComment });
  };

  const handleSubmitReply = (parentId: number) => {
    if (!replyContent.trim()) return;
    addCommentMutation.mutate({ content: replyContent, parentCommentId: parentId });
  };

  const handleUpdateComment = (commentId: number) => {
    if (!editContent.trim()) return;
    updateCommentMutation.mutate({ commentId, content: editContent });
  };

  const handleDeleteComment = (commentId: number) => {
    setDeletingCommentId(commentId);
  };

  const confirmDeleteComment = () => {
    if (deletingCommentId !== null) {
      deleteCommentMutation.mutate(deletingCommentId);
    }
  };

  // Organize comments into threads
  const topLevelComments = comments.filter(c => !c.parentCommentId);
  const getReplies = (parentId: number) => comments.filter(c => c.parentCommentId === parentId);

  // Limit display unless showing all
  const displayedComments = showAllComments ? topLevelComments : topLevelComments.slice(0, 3);

  const renderComment = (comment: Comment, isReply = false) => {
    const replies = getReplies(comment.id);
    const isEditing = editingComment === comment.id;
    const isOwner = user?.id === comment.userId;
    const isAdmin = isAdminRole(user?.role);
    const canModify = isOwner || isAdmin;

    return (
      <div key={comment.id} className={cn("flex gap-3", isReply && "ml-10 mt-3")}>
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={comment.user.photoUrl} alt={comment.user.name} />
          <AvatarFallback className="text-xs">
            {comment.user.name.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="bg-muted rounded-lg px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{comment.user.name}</span>
                {comment.user.title && (
                  <span className="text-xs text-muted-foreground">{comment.user.title}</span>
                )}
              </div>

              {canModify && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isOwner && (
                      <DropdownMenuItem
                        onClick={() => {
                          setEditingComment(comment.id);
                          setEditContent(comment.content);
                        }}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="text-red-600 dark:text-red-400"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {isEditing ? (
              <div className="mt-2 space-y-2">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[60px] text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleUpdateComment(comment.id)}
                    disabled={updateCommentMutation.isPending}
                  >
                    {updateCommentMutation.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingComment(null);
                      setEditContent("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm mt-1 whitespace-pre-wrap">{comment.content}</p>
            )}
          </div>

          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span title={format(new Date(comment.createdAt), "PPP p")}>
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
            </span>
            {comment.isEdited && <span>(edited)</span>}
            {!isReply && (
              <button
                className="hover:text-primary font-medium flex items-center gap-1"
                onClick={() => {
                  setReplyingTo(replyingTo === comment.id ? null : comment.id);
                  setReplyContent("");
                }}
              >
                <Reply className="h-3 w-3" />
                Reply
              </button>
            )}
          </div>

          {/* Reply input */}
          {replyingTo === comment.id && (
            <div className="flex gap-2 mt-3">
              <Avatar className="h-6 w-6 flex-shrink-0">
                <AvatarImage src={user?.photoUrl || undefined} alt={user?.name} />
                <AvatarFallback className="text-xs">
                  {user?.name?.charAt(0).toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 flex gap-2">
                <Textarea
                  placeholder="Write a reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  className="min-h-[40px] text-sm flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitReply(comment.id);
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="h-10"
                  onClick={() => handleSubmitReply(comment.id)}
                  disabled={addCommentMutation.isPending || !replyContent.trim()}
                >
                  {addCommentMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}

          {/* Render replies */}
          {replies.length > 0 && (
            <div className="space-y-2">
              {replies.map(reply => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <MessageCircle className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
        </span>
      </div>

      {/* New comment input */}
      {user && (
        <div className="flex gap-3">
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage src={user.photoUrl || undefined} alt={user.name} />
            <AvatarFallback className="text-xs">
              {user.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Textarea
                placeholder="Write a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[60px] pr-10"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitComment();
                  }
                }}
              />
              <div className="absolute top-2 right-2">
                <VoiceInputButton
                  variant="inline"
                  onTranscript={(text) => setNewComment((prev) => prev + (prev ? " " : "") + text)}
                />
              </div>
            </div>
            <Button
              className="h-[60px]"
              onClick={handleSubmitComment}
              disabled={addCommentMutation.isPending || !newComment.trim()}
            >
              {addCommentMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      {/* Comments list */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3 animate-pulse">
              <div className="h-8 w-8 rounded-full bg-muted" />
              <div className="flex-1">
                <div className="bg-muted rounded-lg px-3 py-2">
                  <div className="h-4 w-24 bg-muted rounded mb-2" />
                  <div className="h-4 w-full bg-muted rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No comments yet. Be the first to comment!
        </p>
      ) : (
        <div className="space-y-4">
          {displayedComments.map(comment => renderComment(comment))}

          {/* Show more/less toggle */}
          {topLevelComments.length > 3 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setShowAllComments(!showAllComments)}
            >
              {showAllComments ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-2" />
                  Show fewer comments
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-2" />
                  Show all {topLevelComments.length} comments
                </>
              )}
            </Button>
          )}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deletingCommentId !== null} onOpenChange={(open) => !open && setDeletingCommentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteComment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
